import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { useAppContext } from '@prisjakt/ui-app-context';
import Breadcrumb, { BreadcrumbPath } from '@prisjakt/ui-breadcrumb';

import HeaderBlock from 'src/areas/product-collection/components/header-block';
import { PageContent } from 'src/components/page';
import { CategoryResults } from './category-results';
import { ProductResults } from './product-results';

import {
  trackKeywordSearchPageNoImages,
  trackKeywordSearchPageOffersOnlyVisited,
} from '../../tracking';

import * as S from './styles';

import { BodyMediumText, H3Text } from '@prisjakt/ui/text/variants';
import { useKWLPWidePageExperiment } from 'src/hooks/experiments';
import { KeywordSearchPageQuery, Offer, Product } from 'src/types';
import {
  trackInternalNavigationClick,
  InternalNavigationScope,
} from 'src/globals/tracking/tracking';

type Props = {
  data: KeywordSearchPageQuery;
};

const KeywordSearchPage = (props: Props) => {
  const { data } = props;
  const reduxDispatch = useDispatch();
  const { t } = useAppContext();

  useEffect(() => {
    reduxDispatch({
      type: 'ADS_VIEW_UPDATE',
      view: `keywordLandingPage`,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduxDispatch]);

  const isWidePage = useKWLPWidePageExperiment();

  if (!data.keywordLandingPage) {
    return null;
  }

  const { url, searchResults, title } = data.keywordLandingPage;
  const { results } = searchResults ?? {};

  const products = results?.products;
  const categories = results?.categories;
  const brands = results?.brands;
  const kwlpTitle = title ?? '';

  // if should send trackKeywordSearchPageOffersOnlyVisited
  const allProducts =
    (products?.nodes.filter(p => p?.__typename === 'Product') as Product[]) ??
    [];

  const allOffers =
    (products?.nodes.filter(p => p?.__typename === 'Offer') as Offer[]) ?? [];

  const isOffersOnly = allProducts?.length === 0 && allOffers.length >= 1;

  if (isOffersOnly && title && url) {
    trackKeywordSearchPageOffersOnlyVisited(title, url);
  }

  // if should send trackKeywordSearchPageNoImages
  const noImagesOfferIDs = allOffers.reduce<string[]>(
    (acc, offer) =>
      !offer.media.first && offer.offerId ? [...acc, offer.offerId] : acc,
    []
  );

  const noImagesProductsIDs = allProducts.reduce<number[]>(
    (acc, product) => (!product.media.first ? [...acc, product.id] : acc),
    []
  );

  const hasItemsWithNoImages =
    noImagesOfferIDs.length > 0 || noImagesProductsIDs.length > 0;

  if (hasItemsWithNoImages) {
    trackKeywordSearchPageNoImages(
      data.keywordLandingPage!.url,
      [...noImagesProductsIDs, ...noImagesOfferIDs].join(',')
    );
  }
  // breadcrumbs path
  let breadCrumbPath: BreadcrumbPath[] = [];
  const { parentCategory } = categories ?? {};

  if (parentCategory && parentCategory.path) {
    const breadCrumbs = Array.isArray(parentCategory.path)
      ? (parentCategory.path as BreadcrumbPath[])
      : [];

    breadCrumbPath = [
      ...breadCrumbs,
      {
        id: kwlpTitle,
        name: `${kwlpTitle[0].toUpperCase()}${kwlpTitle.slice(1)}`,
        pathName: '',
      },
    ];
  }

  // featured store offers are clickable
  const isNotFeaturedStoreOffer = (product: Offer) =>
    product?.__typename === 'Offer' && !product?.store.featured;

  const finalProducts =
    (products?.nodes.length &&
      products?.nodes.filter(
        product => !isNotFeaturedStoreOffer(product as Offer)
      )) ||
    [];

  // if there is data in results
  const noResults =
    (!finalProducts.length &&
      !categories?.nodes.length &&
      !brands?.nodes.length) ||
    !data.keywordLandingPage.published;

  return (
    <PageContent
      withAds
      isOnKwlp
      topPlacements={[]}
      data-test="KeywordSearchPage"
    >
      <Breadcrumb
        homeText={t('common', 'breadcrumb.home')}
        path={breadCrumbPath}
        onClick={(nextPage: string) =>
          trackInternalNavigationClick({
            scope: InternalNavigationScope.BREADCRUMB,
            nextPage,
          })
        }
      />
      <HeaderBlock title={kwlpTitle} />
      {noResults ? (
        <>
          <S.WrapBody>
            <S.BodyGutter>
              <BodyMediumText>
                {t('category', 'keyword-landing-page.no-offers')}
              </BodyMediumText>
            </S.BodyGutter>
          </S.WrapBody>
          <S.WrapBody>
            {categories!.nodes.length > 0 && (
              <>
                <S.BodyGutter>
                  <H3Text>
                    {t('category', 'keyword-landing-page.related-categories')}
                  </H3Text>
                </S.BodyGutter>
                <CategoryResults
                  categories={categories?.nodes}
                  wideContent={false}
                />
              </>
            )}
          </S.WrapBody>
        </>
      ) : (
        <S.SearchResults smallerPadding={isWidePage}>
          <CategoryResults categories={categories?.nodes} wideContent={false} />

          <ProductResults
            products={finalProducts}
            isWide={false}
            title={kwlpTitle}
          />
        </S.SearchResults>
      )}
    </PageContent>
  );
};

export default KeywordSearchPage;
